import { useEffect, useState } from 'react';
import { deactivateMyProfile, updateMyProfile } from '../../api/auth/authApi';
import useAutoClearMessage from '../../hooks/useAutoClearMessage';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

function resolveMarketingConsentValue(user) {
  if (!user || typeof user !== 'object') {
    return null;
  }

  if (typeof user.agreedMarketing === 'boolean') {
    return user.agreedMarketing;
  }
  if (typeof user.marketingAgreed === 'boolean') {
    return user.marketingAgreed;
  }
  if (typeof user.isMarketingAgreed === 'boolean') {
    return user.isMarketingAgreed;
  }

  return null;
}

function resolveAuthProviderValue(user) {
  if (!user || typeof user !== 'object') {
    return 'UNKNOWN';
  }

  const providerCandidates = [
    user.provider,
    user.authProvider,
    user.loginProvider,
    user.socialProvider,
    user.providerType,
  ];

  const rawProvider = providerCandidates.find(
    (value) => typeof value === 'string' && String(value).trim(),
  );
  if (!rawProvider) {
    return 'UNKNOWN';
  }

  const normalizedProvider = String(rawProvider).trim().toUpperCase();
  if (normalizedProvider.includes('KAKAO')) {
    return 'KAKAO';
  }
  if (normalizedProvider.includes('APPLE')) {
    return 'APPLE';
  }
  if (normalizedProvider.includes('EMAIL') || normalizedProvider.includes('LOCAL')) {
    return 'EMAIL';
  }
  return normalizedProvider;
}

function getAuthProviderMeta(provider) {
  const providerMap = {
    KAKAO: {
      label: '카카오 로그인',
      symbol: 'K',
      tone: 'bg-[#fff7d6] text-[#7a4d00] border-[#ffe082]',
    },
    APPLE: {
      label: 'Apple 로그인',
      symbol: 'A',
      tone: 'bg-[#f2f4f7] text-[#101828] border-[#d0d5dd]',
    },
    EMAIL: {
      label: '이메일 로그인',
      symbol: '@',
      tone: 'bg-[#eef4ff] text-[#1d4ed8] border-[#bfdbfe]',
    },
    UNKNOWN: {
      label: '로그인 방식 확인 중',
      symbol: '?',
      tone: 'bg-[#f9fafb] text-[#475467] border-[#eaecf0]',
    },
  };

  return providerMap[provider] || {
    label: `${provider} 로그인`,
    symbol: '•',
    tone: 'bg-[#f9fafb] text-[#475467] border-[#eaecf0]',
  };
}

function ClientMyPage({
  currentUser,
  onLogout,
  onAccountDeactivated,
  onProfileUpdated,
}) {
  const authProvider = resolveAuthProviderValue(currentUser);
  const authProviderMeta = getAuthProviderMeta(authProvider);
  const [nickname, setNickname] = useState(currentUser?.nickname || '');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [agreedMarketing, setAgreedMarketing] = useState(
    () => resolveMarketingConsentValue(currentUser) ?? false,
  );
  const [isSavingMarketing, setIsSavingMarketing] = useState(false);
  const [marketingError, setMarketingError] = useState('');
  const [marketingMessage, setMarketingMessage] = useState('');
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');
  useAutoClearMessage(profileMessage, setProfileMessage);
  useAutoClearMessage(profileError, setProfileError);
  useAutoClearMessage(marketingMessage, setMarketingMessage);
  useAutoClearMessage(marketingError, setMarketingError);
  useAutoClearMessage(deactivateError, setDeactivateError);
  useBodyScrollLock(isDeactivateModalOpen);

  useEffect(() => {
    setNickname(currentUser?.nickname || '');
  }, [currentUser?.nickname]);

  useEffect(() => {
    setAgreedMarketing(resolveMarketingConsentValue(currentUser) ?? false);
  }, [currentUser]);

  const openDeactivateModal = () => {
    setDeactivateError('');
    setIsDeactivateModalOpen(true);
  };

  const closeDeactivateModal = () => {
    if (isDeactivating) {
      return;
    }
    setDeactivateError('');
    setIsDeactivateModalOpen(false);
  };

  const handleDeactivate = async () => {
    if (isDeactivating) {
      return;
    }

    setIsDeactivating(true);
    setDeactivateError('');

    try {
      await deactivateMyProfile();
      if (onAccountDeactivated) {
        onAccountDeactivated();
      } else {
        window.location.replace('/auth/login');
      }
    } catch (error) {
      setDeactivateError(error?.message || '회원탈퇴 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      setIsDeactivating(false);
    }
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();

    if (isSavingProfile) {
      return;
    }

    const trimmedNickname = String(nickname || '').trim();

    if (!trimmedNickname) {
      setProfileError('닉네임을 입력해 주세요.');
      return;
    }
    if (trimmedNickname.length < 2 || trimmedNickname.length > 30) {
      setProfileError('닉네임은 2자 이상 30자 이하여야 합니다.');
      return;
    }
    if (trimmedNickname === String(currentUser?.nickname || '').trim()) {
      setProfileMessage('변경된 내용이 없습니다.');
      setProfileError('');
      return;
    }

    setIsSavingProfile(true);
    setProfileError('');
    setProfileMessage('');

    try {
      const updatedProfile = await updateMyProfile({
        nickname: trimmedNickname,
      });
      setNickname(updatedProfile?.nickname || trimmedNickname);
      setProfileMessage('프로필을 저장했습니다.');
      if (onProfileUpdated) {
        onProfileUpdated(updatedProfile);
      }
    } catch (error) {
      setProfileError(error?.message || '프로필 저장에 실패했습니다.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleToggleMarketingConsent = async () => {
    if (isSavingMarketing || isSavingProfile) {
      return;
    }

    const normalizedNickname = String(
      nickname || currentUser?.nickname || '',
    ).trim();
    if (!normalizedNickname) {
      setMarketingError('닉네임이 비어 있어 마케팅 동의를 저장할 수 없습니다. 먼저 닉네임을 입력해 주세요.');
      return;
    }

    const nextValue = !agreedMarketing;
    setIsSavingMarketing(true);
    setMarketingError('');
    setMarketingMessage('');

    try {
      const updatedProfile = await updateMyProfile({
        nickname: normalizedNickname,
        agreedMarketing: nextValue,
      });
      const savedValue = resolveMarketingConsentValue(updatedProfile);
      const resolvedValue = typeof savedValue === 'boolean' ? savedValue : nextValue;

      setAgreedMarketing(resolvedValue);
      setMarketingMessage(
        resolvedValue
          ? '마케팅 정보 수신 동의를 저장했습니다.'
          : '마케팅 정보 수신 동의를 해제했습니다.',
      );

      if (onProfileUpdated) {
        onProfileUpdated({
          ...(updatedProfile || {}),
          agreedMarketing: resolvedValue,
        });
      }
    } catch (error) {
      setMarketingError(
        error?.message || '마케팅 동의 설정 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.',
      );
    } finally {
      setIsSavingMarketing(false);
    }
  };

  return (
    <main
      className="min-h-screen px-6 py-8 md:px-10 md:py-10"
      style={{
        background:
          'radial-gradient(circle at 8% 0%, #dcfce7 0, transparent 30%), radial-gradient(circle at 100% 10%, #dbeafe 0, transparent 34%), linear-gradient(160deg, #f8fafc 0%, #eef2ff 100%)',
      }}
    >
      <div className="mx-auto flex w-full max-w-[980px] flex-col gap-6">
        <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
          <p className="m-0 inline-block rounded-full bg-[#111827] px-3 py-1 text-xs font-bold tracking-[0.08em] text-white">
            MY
          </p>
          <h1 className="mt-3 text-[clamp(24px,4.5vw,34px)] font-semibold leading-tight tracking-[-0.03em] text-[#0f172a]">
            MY
          </h1>
          <p className="mt-2 text-sm text-[#475467]">
            내 계정 정보를 확인하고 세션을 관리할 수 있습니다.
          </p>

          <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-[#eaecf0] bg-[#f8fafc] px-4 py-3">
            <div className="min-w-0">
              <p className="m-0 text-xs text-[#667085]">현재 로그인 방식</p>
              <p className="mt-1 truncate text-sm font-semibold text-[#101828]">
                {authProviderMeta.label}
              </p>
            </div>
            <span
              className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-full border px-2 text-xs font-bold ${authProviderMeta.tone}`}
              aria-label={`로그인 방식: ${authProviderMeta.label}`}
              title={authProviderMeta.label}
            >
              {authProviderMeta.symbol}
            </span>
          </div>

          <form className="mt-5 grid gap-4" onSubmit={handleSaveProfile}>
            <div className="rounded-xl border border-[#eaecf0] bg-white px-4 py-3">
              <label className="text-xs text-[#667085]" htmlFor="my-email">
                이메일
              </label>
              <input
                id="my-email"
                type="text"
                value={currentUser?.email || '-'}
                readOnly
                className="mt-1 h-11 w-full rounded-lg border border-[#eaecf0] bg-[#f8fafc] px-3 text-base font-semibold text-[#475467] outline-none md:text-sm"
              />
            </div>

            <div className="rounded-xl border border-[#eaecf0] bg-white px-4 py-3">
              <label className="text-xs text-[#667085]" htmlFor="my-nickname">
                닉네임
              </label>
              <input
                id="my-nickname"
                type="text"
                value={nickname}
                onChange={(event) => {
                  setNickname(event.target.value);
                  setProfileError('');
                  setProfileMessage('');
                }}
                maxLength={30}
                className="mt-1 h-11 w-full rounded-lg border border-[#d0d5dd] bg-white px-3 text-base font-semibold text-[#101828] outline-none focus:border-[#667085] md:text-sm"
                placeholder="닉네임을 입력해 주세요"
              />
              <p className="mt-1 text-right text-[11px] text-[#667085]">
                {String(nickname || '').trim().length}/30
              </p>
            </div>

            {profileMessage && (
              <p className="rounded-lg border border-[#abefc6] bg-[#ecfdf3] px-3 py-2 text-sm text-[#027a48]">
                {profileMessage}
              </p>
            )}
            {profileError && (
              <p className="rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                {profileError}
              </p>
            )}

            <div>
              <button
                type="submit"
                disabled={isSavingProfile}
                className="h-10 rounded-lg bg-[#111827] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#98a2b3]"
              >
                {isSavingProfile ? '저장 중...' : '프로필 저장'}
              </button>
            </div>
          </form>

          <section className="mt-5 rounded-2xl border border-[#eaecf0] bg-[#f8fafc] p-4">
            <h2 className="m-0 text-sm font-semibold text-[#0f172a]">약관 및 동의</h2>
            <p className="mt-1 text-xs text-[#667085]">
              필수 항목은 읽기 전용이며, 마케팅 동의만 변경할 수 있습니다.
            </p>

            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between rounded-lg border border-[#eaecf0] bg-white px-3 py-2">
                <span className="text-xs font-semibold text-[#344054]">이용약관</span>
                <span className="rounded-full bg-[#ecfdf3] px-2 py-1 text-[11px] font-semibold text-[#067647]">
                  필수 동의 완료
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-[#eaecf0] bg-white px-3 py-2">
                <span className="text-xs font-semibold text-[#344054]">개인정보 처리방침</span>
                <span className="rounded-full bg-[#ecfdf3] px-2 py-1 text-[11px] font-semibold text-[#067647]">
                  필수 동의 완료
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-[#eaecf0] bg-white px-3 py-2">
                <div>
                  <p className="m-0 text-xs font-semibold text-[#344054]">마케팅 정보 수신</p>
                  <p className="mt-1 text-[11px] text-[#667085]">
                    이벤트/공지성 혜택 알림 수신 여부
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={agreedMarketing}
                  onClick={handleToggleMarketingConsent}
                  disabled={isSavingMarketing}
                  className={`inline-flex h-7 w-12 shrink-0 items-center rounded-full p-1 transition-colors duration-200 ${
                    agreedMarketing ? 'bg-[#111827]' : 'bg-[#d0d5dd]'
                  } disabled:cursor-not-allowed`}
                >
                  <span
                    className={`h-5 w-5 rounded-full bg-white shadow-[0_1px_2px_rgba(16,24,40,0.2)] transition-transform duration-200 ${
                      agreedMarketing ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            {marketingMessage && (
              <p className="mt-3 rounded-lg border border-[#abefc6] bg-[#ecfdf3] px-3 py-2 text-sm text-[#027a48]">
                {marketingMessage}
              </p>
            )}
            {marketingError && (
              <p className="mt-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                {marketingError}
              </p>
            )}
          </section>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                if (onLogout) {
                  onLogout();
                }
              }}
              className="h-10 rounded-lg border border-[#d0d5dd] bg-white px-4 text-sm font-semibold text-[#344054]"
            >
              로그아웃
            </button>
            <button
              type="button"
              onClick={openDeactivateModal}
              className="h-10 rounded-lg border border-[#fda29b] bg-white px-4 text-sm font-semibold text-[#b42318]"
            >
              회원탈퇴
            </button>
          </div>
        </section>
      </div>

      {isDeactivateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#101828]/55 px-5">
          <section className="w-full max-w-[480px] rounded-2xl border border-[#fecdd3] bg-white p-6 shadow-[0_20px_40px_rgba(15,23,42,0.22)]">
            <h2 className="m-0 text-xl font-semibold tracking-[-0.02em] text-[#101828]">회원탈퇴</h2>
            <p className="mt-3 text-sm leading-relaxed text-[#475467]">
              탈퇴하면 계정이 비활성화되고 현재 로그인 세션이 종료됩니다. 진행하시겠습니까?
            </p>

            {deactivateError ? (
              <p className="mt-3 rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-2 text-sm text-[#b42318]">
                {deactivateError}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeDeactivateModal}
                disabled={isDeactivating}
                className="h-10 rounded-lg border border-[#d0d5dd] bg-white px-4 text-sm font-semibold text-[#344054] disabled:cursor-not-allowed disabled:opacity-60"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleDeactivate}
                disabled={isDeactivating}
                className="h-10 rounded-lg bg-[#b42318] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDeactivating ? '탈퇴 처리 중...' : '탈퇴하기'}
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

export default ClientMyPage;
